
import React, { useState, useEffect } from 'react';
import type { Language, WidgetSettings } from '../types';
import { LANGUAGES } from '../constants';
import { useLocalization } from '../hooks/useLocalization';
import { Link, useLocation } from 'react-router-dom';
import { getWidgetSettings } from '../services/dataService';
import { useAuth, initiateGoogleLogin } from '../contexts/AuthContext';
import AuthModal from './auth/AuthModal';
import { Sparkles, Facebook, Instagram, Linkedin, Menu, X, Globe, LogOut, Settings, User, Heart, Briefcase, Plane, Palmtree, Compass, Phone } from 'lucide-react';

interface HeaderProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  onShowSettings: () => void;
}

// Social Icons
const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

// CMS layout types
interface CmsNavLink {
  label: string;
  url: string;
  isActive: boolean;
}
interface CmsHeaderConfig {
  logoUrl?: string;
  logoAlt?: string;
  navLinks?: CmsNavLink[];
  ctaButton?: { text: string; url: string; isVisible: boolean };
  showLanguageSwitcher?: boolean;
  isSticky?: boolean;
}

// Default nav links (fallback when no CMS data)
const DEFAULT_NAV_LINKS: CmsNavLink[] = [
  { label: 'PETRA AI', url: '/tour-assist', isActive: true },
  { label: 'HOLIDAYS', url: '/holidays', isActive: true },
  { label: 'ABOUT US', url: '/about', isActive: true },
];
const DEFAULT_NAV_LINKS_SV: Record<string, string> = {
  'HOLIDAYS': 'PAKETRESOR',
  'PETRA AI': 'PETRA AI',
  'ABOUT US': 'OM OSS',
  'Home': 'Hem',
  'Hotels': 'Hotell',
  'Flights': 'Flyg',
  'About': 'Om Oss',
  'Contact': 'Kontakt',
};
const DEFAULT_NAV_LINKS_DA: Record<string, string> = {
  'HOLIDAYS': 'PAKKEREJSER',
  'PETRA AI': 'PETRA AI',
  'ABOUT US': 'OM OS',
  'Home': 'Hjem',
  'Hotels': 'Hoteller',
  'Flights': 'Fly',
  'About': 'Om Os',
  'Contact': 'Kontakt',
};
const NAV_TRANSLATIONS: Record<string, Record<string, string>> = { sv: DEFAULT_NAV_LINKS_SV, da: DEFAULT_NAV_LINKS_DA };

export const Header: React.FC<HeaderProps> = ({ language, setLanguage }) => {
  const { t } = useLocalization();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState<WidgetSettings>({ campaignActive: false, ctaHotelId: '101083', restrictToWhitelist: false } as WidgetSettings);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [cmsHeader, setCmsHeader] = useState<CmsHeaderConfig | null>(null);



  // Fetch CMS layout config
  useEffect(() => {
    fetch('/api/cms/site-layout')
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.success && data.layout?.header) {
          setCmsHeader(data.layout.header);
        }
      })
      .catch(() => {/* use defaults */ });


  }, []);

  // Listen for settings updates
  useEffect(() => {
    const updateSettings = async () => setSettings(await getWidgetSettings());
    updateSettings(); // Initial load
    window.addEventListener('tourvisio_data_update', updateSettings);
    window.addEventListener('storage', updateSettings);
    return () => {
      window.removeEventListener('tourvisio_data_update', updateSettings);
      window.removeEventListener('storage', updateSettings);
    };
  }, []);


  // Resolve nav links: CMS config → defaults
  const navLinks = (cmsHeader?.navLinks || DEFAULT_NAV_LINKS).filter(l => l.isActive);

  // Apply immersive style to Home, Holidays, and Flights pages
  const isImmersive = (['/', '/holidays', '/flights', '/hotels'].includes(location.pathname)) && !isMobileMenuOpen;

  // Get header top position - use top-0 when no campaign banner
  const headerTopPosition = settings.campaignActive ? 'top-10' : 'top-0';

  // Conditional styles based on route
  const headerClass = isImmersive
    ? `absolute ${headerTopPosition} left-0 w-full z-50 bg-gradient-to-b from-black/70 to-transparent transition-all duration-300`
    : "bg-white border-b border-gray-100 relative z-50";

  // Text color updated to Blue-950 for non-immersive
  const textColor = isImmersive ? "text-white" : "text-blue-950";
  const subTextColor = isImmersive ? "text-white/80" : "text-blue-900/60";
  const iconColor = isImmersive ? "text-white" : "text-blue-950";

  const navLinkClass = isImmersive
    ? "text-white/90 hover:text-white hover:bg-white/10"
    : "text-gray-600 hover:text-blue-950 hover:bg-gray-100";

  const langContainerClass = isImmersive
    ? "bg-white/10 backdrop-blur-md border border-white/20"
    : "bg-gray-100 border border-transparent";

  const langBtnActive = isImmersive
    ? "bg-white text-gray-900"
    : "bg-white text-blue-950 shadow-sm";

  const langBtnInactive = isImmersive
    ? "text-white/80 hover:text-white hover:bg-white/10"
    : "text-gray-500 hover:text-blue-950";

  return (
    <header className={headerClass}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <div className="flex items-center gap-12">
            {/* Logo */}
            <div className="flex-shrink-0 z-50">
              <Link to="/" className="flex items-center group gap-3" onClick={() => setIsMobileMenuOpen(false)}>
                <div className="flex flex-col">
                  <span className={`text-2xl font-black tracking-tight leading-none ${textColor}`}>
                    LUMI <span className={isImmersive ? "text-white" : "text-blue-950"}>TOURS</span>
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${subTextColor}`}>
                    Premium Travel
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation — CMS-driven */}
            <nav className="hidden lg:flex space-x-1">
              {navLinks.map((link, i) => {
                const isPetraAI = link.url === '/tour-assist';
                const isActive = location.pathname === link.url;
                const displayLabel = isPetraAI ? 'PETRA AI' : (NAV_TRANSLATIONS[language]?.[link.label] || link.label);

                if (isPetraAI) {
                  return (
                    <Link
                      key={i}
                      to={link.url}
                      className="relative flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-xs uppercase tracking-widest text-white bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 transition-all duration-300 group overflow-hidden"
                    >
                      {/* Shimmer overlay */}
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] animate-shimmer" />
                      <Sparkles size={14} className="relative z-10 text-white" />
                      <span className="relative z-10">{displayLabel}</span>
                    </Link>
                  );
                }

                return (
                  <Link
                    key={i}
                    to={link.url}
                    className={`px-5 py-2.5 rounded-full font-bold transition-all text-xs uppercase tracking-widest ${navLinkClass} ${isActive ? 'bg-blue-600/10 text-blue-600' : ''}`}
                  >
                    {displayLabel}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-4">

            {/* Social Media Icons (Desktop) */}
            <div className={`hidden md:flex items-center gap-3 mr-2 ${isImmersive ? 'text-white/80' : 'text-gray-500'}`}>
              {(settings.facebookUrl || settings.facebookUrl === undefined) && (
                <a href={settings.facebookUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors transform hover:scale-110">
                  <FacebookIcon />
                </a>
              )}
              {(settings.instagramUrl || settings.instagramUrl === undefined) && (
                <a href={settings.instagramUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors transform hover:scale-110">
                  <InstagramIcon />
                </a>
              )}
              {(settings.linkedinUrl || settings.linkedinUrl === undefined) && (
                <a href={settings.linkedinUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors transform hover:scale-110">
                  <LinkedInIcon />
                </a>
              )}
            </div>

            {/* Language Selector (Desktop) */}
            <div className="hidden md:flex items-center space-x-4">
              <div className={`flex items-center rounded-full p-1 transition-colors ${langContainerClass}`}>
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all duration-200 uppercase
                        ${language === lang.code ? langBtnActive : langBtnInactive}
                      `}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>

              {/* Auth Buttons */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${isImmersive ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`}
                  >
                    {user?.avatarUrl ? (
                      <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isImmersive ? 'bg-white/20' : 'bg-blue-600 text-white'}`}>
                        {user?.profile?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium hidden lg:inline">
                      {user?.profile?.firstName || user?.email?.split('@')[0]}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100">
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Admin Panel
                        </Link>
                      )}
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {language === 'sv' ? 'Min Profil' : language === 'da' ? 'Min Profil' : 'My Profile'}
                      </Link>
                      <Link
                        to="/bookings"
                        onClick={() => setShowUserMenu(false)}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {language === 'sv' ? 'Mina Bokningar' : language === 'da' ? 'Mine Bookinger' : 'My Bookings'}
                      </Link>
                      <hr className="my-2" />
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        {language === 'sv' ? 'Logga ut' : language === 'da' ? 'Log ud' : 'Logout'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setAuthModalMode('login'); setShowAuthModal(true); }}
                    className={`px-5 py-2.5 text-sm font-semibold rounded-full transition-all ${isImmersive ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                  >
                    {language === 'sv' ? 'Logga in' : language === 'da' ? 'Log ind' : 'Login'}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg z-50 transition-colors ${isImmersive ? 'text-white hover:bg-white/10' : 'text-blue-950 hover:bg-gray-100'}`}
            >
              {isMobileMenuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-0 left-0 w-full h-screen bg-white z-40 pt-24 px-6 animate-fade-in flex flex-col">
          <nav className="flex flex-col space-y-4 text-center mt-8">
            {navLinks.map((link, i) => {
              const isPetraAI = link.url === '/tour-assist';
              const displayLabel = isPetraAI ? 'PETRA AI' : (NAV_TRANSLATIONS[language]?.[link.label] || link.label);

              if (isPetraAI) {
                return (
                  <Link
                    key={i}
                    to={link.url}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="relative flex items-center justify-center gap-3 text-xl font-bold text-white py-4 mx-4 rounded-2xl bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 shadow-lg shadow-purple-500/25 overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] animate-shimmer" />
                    <Sparkles size={18} className="relative z-10" />
                    <span className="relative z-10">{displayLabel}</span>
                  </Link>
                );
              }

              return (
                <Link
                  key={i}
                  to={link.url}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-2xl font-bold text-gray-900 hover:text-black py-3 border-b border-gray-100"
                >
                  {displayLabel}
                </Link>
              );
            })}
          </nav>

          <div className="mt-8 flex justify-center">
            <div className="flex items-center rounded-full p-1 bg-gray-100 border border-gray-200">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-6 py-3 text-sm font-bold rounded-full transition-all duration-200 uppercase
                        ${language === lang.code ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}
                      `}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          {/* Social Icons (Mobile) */}
          <div className="mt-8 flex justify-center gap-6 text-gray-600">
            {(settings.facebookUrl || settings.facebookUrl === undefined) && (
              <a href={settings.facebookUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors p-2 bg-gray-50 rounded-full">
                <FacebookIcon />
              </a>
            )}
            {(settings.instagramUrl || settings.instagramUrl === undefined) && (
              <a href={settings.instagramUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors p-2 bg-gray-50 rounded-full">
                <InstagramIcon />
              </a>
            )}
            {(settings.linkedinUrl || settings.linkedinUrl === undefined) && (
              <a href={settings.linkedinUrl || '#'} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 transition-colors p-2 bg-gray-50 rounded-full">
                <LinkedInIcon />
              </a>
            )}
          </div>

          {/* Auth Section (Mobile) */}
          <div className="mt-8 flex flex-col items-center gap-3">
            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-3 text-gray-700">
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-lg font-bold">
                      {user?.profile?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-lg font-semibold">
                    {user?.profile?.firstName || user?.email?.split('@')[0]}
                  </span>
                </div>
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full text-center px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all"
                  >
                    Admin Panel
                  </Link>
                )}
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full text-center px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                >
                  {language === 'sv' ? 'Min Profil' : language === 'da' ? 'Min Profil' : 'My Profile'}
                </Link>
                <button
                  onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                  className="w-full px-6 py-3 text-red-600 border border-red-200 rounded-xl font-semibold hover:bg-red-50 transition-all"
                >
                  {language === 'sv' ? 'Logga ut' : language === 'da' ? 'Log ud' : 'Logout'}
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-3 w-full max-w-xs">
                <button
                  onClick={() => { setAuthModalMode('login'); setShowAuthModal(true); setIsMobileMenuOpen(false); }}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
                >
                  {language === 'sv' ? 'Logga in' : language === 'da' ? 'Log ind' : 'Login'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authModalMode}
      />

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;

